import {AfterViewInit, Component, DestroyRef, effect, inject, OnDestroy, OnInit} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router, RouterLink} from '@angular/router';
import {AuthenticationService} from '../../../core/service/auth.service';
import {MENU_ITEMS, MenuItem} from '../models/menu.model';
import {findAllParent} from '../helper/utils';
import {Subscription} from 'rxjs';
import {NgClass} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';
import {LayoutService} from "../layout.service";
import {IconComponent} from "../../../shared/components/icon/icon.component";
import {SidebarProfileDropdownComponent} from "../components/sidebar-profile-dropdown/sidebar-profile-dropdown.component";
import {WhatsNewService} from '../../../apps/whats-new/shared/service/whats-new.service';
import {GgTooltipDirective} from '../../../shared/components/gg-tooltip.directive';
import {FeatureFlagService} from '../../../core/feature-flags/feature-flag.service';
import {UsageChipComponent} from '../../../shared/subscription-overview/usage-chip.component';
import {DevIntentSwitcherComponent} from '../topbar/dev-intent-switcher/dev-intent-switcher.component';

interface GroupedMenuItems {
    available: MenuItem[];
    unavailable: MenuItem[];
    groupTitle?: string;
    groupKey?: string;
}

@Component({
    selector: 'app-left-sidebar',
    templateUrl: './left-sidebar.component.html',
    styleUrls: ['./left-sidebar.component.scss'],
    imports: [RouterLink, NgClass, TranslatePipe, IconComponent, SidebarProfileDropdownComponent, GgTooltipDirective, UsageChipComponent, DevIntentSwitcherComponent]
})
export class LeftSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
    currentUserSubscriptions: string[] = [];
    activeMenuItems: string[] = [];
    private readonly routerSubscription: Subscription;
    private readonly menuItems: MenuItem[] = JSON.parse(JSON.stringify(MENU_ITEMS));

    groupedMenuItems: GroupedMenuItems[] = [];
    discoverMoreCollapsed = true;
    hasFreeSubscription = false;

    private readonly destroyRef = inject(DestroyRef);
    private readonly featureFlagService = inject(FeatureFlagService);

    /** Loaded feature-flag set for the current user/tenant — empty until the request resolves. */
    private readonly flags = toSignal(this.featureFlagService.flags(), {initialValue: new Set<string>()});

    constructor(
        private readonly router: Router,
        private readonly authService: AuthenticationService,
        private readonly layoutService: LayoutService,
        protected readonly whatsNewService: WhatsNewService,
    ) {
        this.routerSubscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this._activateMenu();
                this.closeMobileMenuOnNavigation();
            }
        });

        // Flags load asynchronously (HTTP) — re-group the menu once they arrive so
        // flag-gated items (e.g. Integrations) appear without requiring a reload.
        effect(() => {
            this.flags();
            this.groupMenuItems();
        });
    }

    ngOnInit(): void {
        this.menuItems.forEach(menu => {
            if (menu.children) {
                if (menu.collapsed === undefined) menu.collapsed = true;
                menu.children.forEach(child => {
                    if (child.children && child.collapsed === undefined) child.collapsed = true;
                });
            }
        });

        this.authService.getSubscriptionChanges()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((subscriptions) => {
                this.currentUserSubscriptions = subscriptions ?? [];
                this.groupMenuItems();
                this.hasFreeSubscription = subscriptions?.some(s => s === 'guideglare_zero') ?? false;
            });

        this.groupMenuItems();
    }

    ngAfterViewInit(): void {
        setTimeout(() => this._activateMenu(), 0);
    }

    ngOnDestroy(): void {
        this.routerSubscription?.unsubscribe();
    }

    toggleSidebar(): void {
        this.layoutService.toggleSidebar();
    }

    // ─── Menu helpers ────────────────────────────────────────────────────────────

    groupMenuItems(): void {
        this.groupedMenuItems = [];
        let currentGroup: GroupedMenuItems | null = null;
        let currentGroupTitle = '';
        let currentGroupKey = '';

        for (const item of this.menuItems) {
            if (item.isTitle) {
                if (currentGroup && currentGroup.unavailable.length > 0) {
                    currentGroup.groupTitle = currentGroupTitle;
                    currentGroup.groupKey = currentGroupKey;
                    this.groupedMenuItems.push(currentGroup);
                }
                currentGroupTitle = item.label;
                currentGroupKey = item.key || '';
                currentGroup = {available: [], unavailable: []};
                continue;
            }
            if (item.isSeparator) continue;
            if (!currentGroup) currentGroup = {available: [], unavailable: []};
            if (this.isAvailable(item)) {
                currentGroup.available.push(item);
            } else if (this.shouldShowInDiscoverMore(item)) {
                currentGroup.unavailable.push(item);
            }
        }

        if (currentGroup && currentGroup.unavailable.length > 0) {
            currentGroup.groupTitle = currentGroupTitle;
            currentGroup.groupKey = currentGroupKey;
            this.groupedMenuItems.push(currentGroup);
        }
    }

    getAvailableMenuItems(): MenuItem[] {
        const availableItems: MenuItem[] = [];
        let currentTitleItem: MenuItem | null = null;

        for (const item of this.menuItems) {
            if (item.isTitle) { currentTitleItem = item; continue; }
            if (item.isSeparator) { availableItems.push(item); continue; }
            if (this.isAvailable(item)) {
                if (currentTitleItem) { availableItems.push(currentTitleItem); currentTitleItem = null; }
                availableItems.push(item);
            }
        }
        return availableItems;
    }

    toggleDiscoverMore(): void {
        this.discoverMoreCollapsed = !this.discoverMoreCollapsed;
    }

    closeMobileMenuOnNavigation(): void {
        if (window.innerWidth < 992) {
            const drawer = document.getElementById('left-sidebar-drawer') as HTMLInputElement;
            if (drawer) drawer.checked = false;
        }
    }

    _activateMenu(): void {
        const currentUrl = this.router.url;

        const buildItemUrl = (item: MenuItem): string => {
            if (!item.link) return '';
            if (!item.queryParams) return item.link;
            const params = Object.entries(item.queryParams).map(([k, v]) => `${k}=${v}`).join('&');
            return `${item.link}?${params}`;
        };

        const findMenuItemByUrl = (items: MenuItem[], url: string): MenuItem | null => {
            for (const item of items) {
                if (item.link && url === buildItemUrl(item)) return item;
                if (item.children?.length) {
                    const found = findMenuItemByUrl(item.children, url);
                    if (found) return found;
                }
            }
            return null;
        };

        const foundMenuItem = findMenuItemByUrl(this.menuItems, currentUrl);
        this.activeMenuItems = [];
        if (!foundMenuItem) return;
        if (foundMenuItem.key) this.activeMenuItems.push(foundMenuItem.key);
        this.activeMenuItems = [...this.activeMenuItems, ...findAllParent(this.menuItems, foundMenuItem)];

        this.menuItems.forEach((menu: MenuItem) => {
            if (this.activeMenuItems.includes(menu.key!)) menu.collapsed = false;
            if (menu.children) {
                const hasActiveChild = menu.children.some(child =>
                    this.activeMenuItems.includes(child.key!) ||
                    child.children?.some(gc => this.activeMenuItems.includes(gc.key!))
                );
                if (hasActiveChild) {
                    if (menu.key && !this.activeMenuItems.includes(menu.key)) this.activeMenuItems.push(menu.key);
                    menu.collapsed = false;
                }
                menu.children.forEach((child: MenuItem) => {
                    if (child.children?.some(gc => this.activeMenuItems.includes(gc.key!))) {
                        if (child.key && !this.activeMenuItems.includes(child.key)) this.activeMenuItems.push(child.key);
                        child.collapsed = false;
                    }
                });
            }
        });
    }

    toggleMenuItem(menuItem: MenuItem): void {
        if (menuItem.collapsable === undefined || menuItem.collapsable) {
            menuItem.collapsed = !menuItem.collapsed;
            if (!menuItem.collapsed) {
                const parent = this.findParentItem(menuItem);
                const siblings = parent?.children ?? this.menuItems;
                siblings.forEach((menu: MenuItem) => {
                    if (menu.key !== menuItem.key && (menu.collapsable === undefined || menu.collapsable)) {
                        menu.collapsed = true;
                    }
                });
            }
        }
    }

    findParentItem(menuItem: MenuItem): MenuItem | null {
        for (const menu of this.menuItems) {
            if (menu.children) {
                for (const child of menu.children) {
                    if (child.key === menuItem.key) return menu;
                    if (child.children?.some(gc => gc.key === menuItem.key)) return child;
                }
            }
        }
        return null;
    }

    get tooltipPlacement(): 'right' | 'bottom' {
        return window.innerWidth < 992 ? 'bottom' : 'right';
    }

    hasSubmenu(menu: MenuItem): boolean {
        return !!menu.children?.length;
    }

    isAvailable(menuItem: MenuItem): boolean {
        if (menuItem.featureFlag && !this.flags().has(menuItem.featureFlag)) return false;
        if (!menuItem.necessaryRoles) return true;
        if (menuItem.necessaryRoles === '*' && this.currentUserSubscriptions?.length > 0) return true;
        if (menuItem.necessaryRoles !== '*' && this.currentUserSubscriptions?.some(s => menuItem.necessaryRoles.includes(s))) return true;
        return false;
    }

    shouldShowInDiscoverMore(menuItem: MenuItem): boolean {
        return menuItem.hideWhenUnavailable !== true;
    }

}
